import { FileExtension, FileId, FileType, UploadedFile } from "@inrev/common";
import { useFileUpload } from "@portal/api/bond-request/agent";
import { useFileStatusDispatch } from "@portal/providers/FileStatusProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiCheck } from "react-icons/hi2";
import { v4 } from "uuid";
import type { AttachmentContext } from "../../analytics";
import { UploadFile } from "../../types";
import { getNameAndExtensionFromFileName } from "../../utils";
import { Modal } from "../layout/Modal";
import { cn } from "../lib/utils";
import { RadioChip, RadioGroup } from "../primitives";
import { Button } from "./Button";
import { CheckboxOption } from "./CheckboxOption";
import { Dropzone } from "./Dropzone";
import { FileUploadItem, PendingFileUploadItem } from "./FileUploadItem";

export type LocalFileUploadProps<
	TFileType extends FileType = FileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
> = {
	value: UploadFile<TFileType>[];
	onChange: (value: UploadFile<TFileType>[]) => void;
	onBlur?: () => void;
	allowedTypesAndLabels: TAllowedTypesAndLabels;
	allowedExtensions?: FileExtension[];
	requiredTypes?: TRequired;
	typeQuestionText?: string;
	maxTypesPerFile?: number;
	subsequentFileTypeRestriction?: true;
	maxFiles?: number;
	hideChecklist?: true;
	showErrors?: boolean;
	allowDelete?: boolean;
	className?: string;
	dropzonePlaceholderText?: string;
	analyticsContext: AttachmentContext;
};

export const LocalFileUpload = <
	TFileType extends FileType = FileType,
	TAllowedTypesAndLabels extends Partial<Record<TFileType, string>> = Partial<
		Record<TFileType, string>
	>,
	TAllowedType extends Extract<TFileType, keyof TAllowedTypesAndLabels> = Extract<
		TFileType,
		keyof TAllowedTypesAndLabels
	>,
	TRequired extends TAllowedType[] | undefined = TAllowedType[] | undefined,
>({
	value: files,
	onChange: setFiles,
	onBlur,
	allowedTypesAndLabels,
	allowedExtensions,
	requiredTypes,
	typeQuestionText,
	maxTypesPerFile,
	subsequentFileTypeRestriction,
	maxFiles,
	showErrors,
	hideChecklist,
	allowDelete,
	className,
	dropzonePlaceholderText,
	analyticsContext,
}: LocalFileUploadProps<TFileType, TAllowedTypesAndLabels, TAllowedType, TRequired>) => {
	const [filesPendingUpload, setFilesPendingUpload] = useState<UploadFile<TFileType>[]>([]);
	const [showTypeSelection, setShowTypeSelection] = useState<boolean>(false);
	const [filesForTypeSelection, setFilesForTypeSelection] = useState<File[] | undefined>();
	const [selectedTypes, setSelectedTypes] = useState<TFileType[]>([]);
	const { setIsFileStatusPending } = useFileStatusDispatch();

	const allowedTypes = useMemo(() => {
		const types = Object.keys(allowedTypesAndLabels);
		if (!types.length) throw new Error();
		return types as [TFileType, ...TFileType[]];
	}, [allowedTypesAndLabels]);

	const atLeastOneSelectedType = (
		selectedTypes: TFileType[],
	): selectedTypes is [TFileType, ...TFileType[]] => {
		return selectedTypes.length > 0;
	};

	const requiredTypesFulfilled = useMemo(() => {
		if (!requiredTypes?.length) return true;
		return requiredTypes.every((requiredType) =>
			files.some((file) => file.types.includes(requiredType)),
		);
	}, [requiredTypes, files]);

	const { uploadFn, fileLoading } = useFileUpload<TFileType>({
		onUploadSuccess: useCallback(
			(uploadedFiles: UploadedFile<TFileType>[], args) => {
				setFilesPendingUpload((pending) =>
					pending.filter((p) => !args.fileUploads.some((u) => u.pendingFile.id === p.id)),
				);
				setFiles([...files, ...uploadedFiles.map((f) => ({ ...f }))]);
			},
			[files, setFiles],
		),
		onUploadError: (_error, args) => {
			setFilesPendingUpload((pending) =>
				pending.filter((p) => !args.fileUploads.some((u) => u.pendingFile.id === p.id)),
			);
		},
	});

	useEffect(() => {
		if (fileLoading) {
			setIsFileStatusPending(true);
			return () => setIsFileStatusPending(false);
		}
	}, [fileLoading, setIsFileStatusPending]);

	const uploadFiles = (rawFiles: File[], types: TFileType[]) => {
		const fileUploads = rawFiles.map((rawFile) => {
			const { fileName, extension } = getNameAndExtensionFromFileName(rawFile.name);
			return {
				rawFile,
				pendingFile: { id: v4() as FileId, name: fileName, extension, types },
			};
		});
		setFilesPendingUpload((pending) => [...pending, ...fileUploads.map((u) => u.pendingFile)]);
		uploadFn({ fileUploads });
	};

	const handleUpload = (dropzoneFiles: File[]) => {
		if (allowedTypes.length === 1) {
			uploadFiles(dropzoneFiles, allowedTypes);
		} else if (subsequentFileTypeRestriction && (files.length || filesPendingUpload.length)) {
			const existingTypes = files[0]?.types ?? filesPendingUpload[0]?.types ?? allowedTypes;
			uploadFiles(dropzoneFiles, existingTypes);
		} else {
			setFilesForTypeSelection(dropzoneFiles);
			setShowTypeSelection(true);
		}
	};

	const handleUploadTypeSelection = (selectedTypes: TFileType[]) => {
		if (!atLeastOneSelectedType(selectedTypes)) return;
		if (!filesForTypeSelection) return;
		uploadFiles(filesForTypeSelection, selectedTypes);
		setSelectedTypes([]);
		setShowTypeSelection(false);
	};

	const handleDelete = (id: string) => {
		setFiles(files.filter((file) => file.id !== id));
	};

	const onBlurRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (onBlur) onBlurRef.current = onBlur;
	}, [onBlur]);

	useEffect(() => {
		return () => {
			onBlurRef.current = null;
		};
	}, []);

	useEffect(() => {
		onBlurRef.current?.();
	}, [files]);

	return (
		<div className={cn("relative flex flex-col space-y-[25px] w-full", className)}>
			{requiredTypes && !!requiredTypes.length && !hideChecklist && (
				<div
					className={cn(
						"relative flex-1 flex flex-col space-y-[5px] pl-[23px] after:absolute after:w-[3px] after:rounded-full after:bg-gray-100 after:top-0 after:bottom-0 after:left-0",
						showErrors && !requiredTypesFulfilled ? "after:bg-red-100" : undefined,
					)}
				>
					{requiredTypes.map((type) => {
						const typeIncluded = files.some((file) => file.types?.includes(type));
						return (
							<div
								key={type}
								className={cn(
									"flex items-center space-x-[10px] text-[15px]",
									typeIncluded ? "opacity-100" : "opacity-70",
									showErrors && !typeIncluded ? "text-red-500 opacity-100" : undefined,
								)}
							>
								<div
									className={cn(
										"relative aspect-square h-4 w-4 rounded-full border border-gray-400 bg-white focus:outline-none flex items-center justify-center",
										showErrors && !typeIncluded ? "border-red-500" : undefined,
									)}
								>
									{typeIncluded && (
										<div className="flex items-center justify-center absolute top-[-1px] left-[-1px] aspect-square h-4 w-4 rounded-full bg-brand-accent ml-[-1] mt-[-1]">
											<HiCheck className="text-[10px] text-inverse stroke-[4px]" />
										</div>
									)}
								</div>
								<div>{allowedTypesAndLabels[type]}</div>
							</div>
						);
					})}
				</div>
			)}
			<div className="w-full flex flex-col space-y-[15px]">
				{showErrors &&
					!requiredTypesFulfilled &&
					hideChecklist &&
					requiredTypes &&
					!!requiredTypes.length && <div className="text-[13px] text-red-500">Required</div>}
				{(maxFiles === undefined || files.length + filesPendingUpload.length < maxFiles) && (
					<Dropzone
						allowedExtensions={allowedExtensions}
						onSuccess={handleUpload}
						placeholderText={dropzonePlaceholderText}
					/>
				)}
				{(files.length > 0 || filesPendingUpload.length > 0) && (
					<div className="w-full max-w-full flex flex-col space-y-[10px] cursor-default">
						{files.map((file) => (
							<FileUploadItem
								key={file.id}
								{...file}
								typesConfirmedByAdmin={false}
								enableTypeSelection={false}
								typeLabelMap={allowedTypesAndLabels}
								allowDelete={allowDelete}
								onDelete={handleDelete}
								enablePDFViewer={false}
								deleting={false}
								analyticsContext={analyticsContext}
							/>
						))}
						{filesPendingUpload.map((file) => (
							<PendingFileUploadItem key={file.id} {...file} typeLabelMap={allowedTypesAndLabels} />
						))}
					</div>
				)}
			</div>
			{showTypeSelection && (
				<Modal onClickOutside={() => setShowTypeSelection(false)}>
					<div
						className={cn(
							"py-[35px] px-[35px] bg-white flex flex-col justify-center rounded-md shadow-lg space-y-[35px]",
							className,
						)}
					>
						<div className="text-[16px] text-secondary font-thin">
							{typeQuestionText ?? "What type of file is this?"}
						</div>
						{maxTypesPerFile === 1 && (
							<RadioGroup<TFileType | undefined>
								value={undefined}
								onChange={(value) => {
									if (value !== undefined) handleUploadTypeSelection([value]);
								}}
								direction="column"
								className="gap-[35px]"
							>
								{allowedTypes.map((type) => (
									<RadioChip value={type} key={type}>
										{allowedTypesAndLabels[type]}
									</RadioChip>
								))}
							</RadioGroup>
						)}
						{maxTypesPerFile !== 1 && (
							<>
								{allowedTypes.map((type) => {
									const checked = selectedTypes.includes(type);
									return (
										<CheckboxOption
											value={type}
											checked={checked}
											onChange={(updateChecked) =>
												setSelectedTypes(
													!updateChecked
														? selectedTypes.filter((t) => t !== type)
														: [...selectedTypes, type],
												)
											}
											label={allowedTypesAndLabels[type]}
											key={type}
										/>
									);
								})}
								<Button
									color="primary"
									filled
									onClick={() => handleUploadTypeSelection(selectedTypes)}
								>
									Upload
								</Button>
							</>
						)}
					</div>
				</Modal>
			)}
		</div>
	);
};
